// Bilingual text lives next to the content ({ vi, en }); this module only picks the active language.
// Korean is a third language kept out of the content: src/i18n/ko/*.json map each English text to its Korean
// translation (scripts/i18n-ko.mjs lists what is missing). A text without a Korean entry falls back to English.
export type Lang = "vi" | "en" | "ko";
export type T = { vi: string; en: string };
export const LANGS: Lang[] = ["vi", "en", "ko"];

const STORE_KEY = "ll-lang";

function detect(): Lang {
  const q = new URLSearchParams(location.search).get("lang");
  let saved: string | null = null;
  try { saved = localStorage.getItem(STORE_KEY); } catch { /* private mode */ }
  const l = q ?? saved;
  return l === "en" || l === "ko" ? l : "vi";
}

export const lang: Lang = detect();

/** English text → Korean, filled by loadKo() before the app starts. */
const KO = new Map<string, string>();
/** English texts shown in Korean mode without a translation (tests read it through window.__koMissing). */
const missing = new Set<string>();
(window as unknown as { __koMissing: Set<string> }).__koMissing = missing;

const ko = (en: string): string => {
  const k = KO.get(en);
  if (k !== undefined) return k;
  missing.add(en);
  return en;
};

const KO_FILES = import.meta.glob<{ default: Record<string, string> }>("../i18n/ko/*.json");
const koLoaded = new Set<string>();
/** Dictionary name of a file path: "../i18n/ko/learn-lean.json" → "learn-lean". */
const koName = (path: string) => path.slice(path.lastIndexOf("/") + 1, -5);

/**
 * Loads the Korean dictionaries (one per content chunk) whose name passes `want`; no-op outside Korean mode and for
 * dictionaries already loaded. The 3D app needs core + the modules; the document pages add the academy (learn-*) and
 * the glossary (glossary-*) — about 1 MB the 3D app never has to download.
 */
export async function loadKo(want: (name: string) => boolean = n => !n.startsWith("learn-") && !n.startsWith("glossary-")): Promise<void> {
  if (lang !== "ko") return;
  const todo = Object.entries(KO_FILES).filter(([path]) => { const n = koName(path); return want(n) && !koLoaded.has(n); });
  todo.forEach(([path]) => koLoaded.add(koName(path)));
  for (const d of await Promise.all(todo.map(([, f]) => f()))) for (const [en, k] of Object.entries(d.default)) KO.set(en, k);
}

/** Text in the active language. Plain strings (codes, names) pass through unchanged. */
export const tr = (t: T | string): string => (typeof t === "string" ? t : lang === "ko" ? ko(t.en) : t[lang]);

/** Inline text for UI chrome: L("Đóng", "Close"). Pass `k` when the English text is built at run time. */
export const L = (vi: string, en: string, k?: string): string => (lang === "en" ? en : lang === "ko" ? (k ?? ko(en)) : vi);

/** Switching language reloads the page so every scene and label is rebuilt in one language. */
export function setLang(next: Lang): void {
  try { localStorage.setItem(STORE_KEY, next); } catch { /* ignore */ }
  const url = new URL(location.href);
  url.searchParams.set("lang", next);
  location.href = url.toString();
}
